import type { PageHint } from '../types';

/**
 * Where the employer assistant is, and what it suggests there. Focus screens
 * are a live interview and the full-screen job editors, whose own buttons
 * sit where the orb would.
 */
export const FOCUS_SCREENS = [
  /^\/interview\/[^/]+\/join/,
  /^\/jobs\/new\/draft/,
  /^\/job\/post/,
  /^\/job-posted/,
];

/** Pages with the employer bottom bar on small screens (EmployerBottomAppBar). */
export const BOTTOM_BAR_PAGES = [
  /^\/jobs\/?$/,
  /^\/unlocked-candidates/,
  /^\/interviews/,
  /^\/team-management/,
  /^\/orgs\//,
  /^\/chat\/employer(\/[^/]+)?\/?$/,
  /^\/employer-profile/,
  /^\/employer\/settings/,
  /^\/billing-and-plans/,
  /^\/applicant-review\/[^/]+/,
];

export const PAGE_HINTS: PageHint[] = [
  {
    pattern: /^\/applicant-review\/(?!posting-preview)[^/]+\/[^/]+/,
    label: 'This applicant',
    suggestions: [
      'Is this candidate a good fit?',
      'What skills are they missing?',
      'Shortlist this candidate',
    ],
  },
  {
    pattern: /^\/applicant-review\/(?!posting-preview)[^/]+/,
    label: "This job's applicants",
    suggestions: [
      'Show only the new applicants',
      'Who are the top 5 matches?',
      'Candidates with 3+ years experience',
    ],
  },
  {
    pattern: /^\/jobs\/[^/]+\/dashboard/,
    label: 'This job',
    suggestions: [
      'How is this job doing?',
      'Show me the best new applicants',
      'Who should I shortlist first?',
    ],
  },
  {
    pattern: /^\/jobs\/[^/]+\/invite-applicants/,
    label: 'Invite candidates',
    suggestions: [
      'How does inviting candidates work?',
      'Show me the applicants so far',
      'How is this job doing?',
    ],
  },
  {
    pattern: /^\/jobs\/?$/,
    label: 'Your jobs',
    suggestions: [
      'What needs my attention today?',
      'Which job has the most new applicants?',
      'Post a new job with AI',
    ],
  },
  {
    pattern: /^\/jobs\/new/,
    label: 'Post with AI',
    suggestions: [
      'Help me describe the role',
      'What makes a good job post?',
      'Is posting a job free?',
    ],
  },
  {
    pattern: /^\/unlocked-candidates/,
    label: 'Unlocked candidates',
    suggestions: [
      'Who have I unlocked but not interviewed?',
      'Schedule an interview',
      'How much does unlocking cost?',
    ],
  },
  {
    pattern: /^\/interviews/,
    label: 'Interviews',
    suggestions: [
      'What interviews do I have today?',
      'Which interviews are this week?',
      'Schedule an interview',
    ],
  },
  {
    pattern: /^\/chat\/employer/,
    label: 'Messages',
    suggestions: [
      'Which jobs have unread messages?',
      'Write a message inviting them to interview',
      'What needs my attention today?',
    ],
  },
  {
    pattern: /^\/team-management/,
    label: 'Your team',
    suggestions: [
      "Who's on my hiring team?",
      'Invite a teammate',
      'What can a manager do?',
    ],
  },
  {
    pattern: /^\/billing-and-plans/,
    label: 'Credits',
    suggestions: [
      'How many credits do I have?',
      'What does unlocking a candidate cost?',
      'What is AI ranking?',
    ],
  },
];

export const GENERAL_SUGGESTIONS = [
  'What needs my attention today?',
  'Any new applicants?',
  'How many credits do I have?',
];

/** Ways into a feature, each skipped on its own pages. */
export const EXPLORE_SUGGESTIONS: { pattern: RegExp; text: string }[] = [
  { pattern: /^\/jobs\/?$/, text: 'Which job needs attention?' },
  { pattern: /^\/jobs\/new/, text: 'Post a new job with AI' },
  { pattern: /^\/interviews/, text: 'Any interviews today?' },
  { pattern: /^\/unlocked-candidates/, text: 'Show my unlocked candidates' },
  { pattern: /^\/applicant-review/, text: 'Show the best new applicants' },
  { pattern: /^\/team-management/, text: 'Invite a teammate' },
];

/** Cycled through the empty composer: kept short so it never wraps on a phone. */
export const TRY_SAYING = [
  'Show React devs with 3+ yrs',
  'Only new applicants',
  'Shortlist the top 3',
  'Sort by best match',
  'Post a data analyst job',
  'Any interviews today?',
];
